import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatPrice, colorHex, img } from "@/lib/api";
import { getAlt } from "@/lib/imageSeo";
import { productName } from "@/lib/product";
import { localizeCategory } from "@/lib/category";
import { useCategories } from "@/context/CategoriesContext";

const ease = [0.16, 1, 0.3, 1];

export const ProductCard = ({ product, index = 0 }) => {
  const { i18n } = useTranslation();
  const { categories } = useCategories();
  const catLabel = localizeCategory(categories, product.category, i18n.language);
  const onSale = product.compare_at_price > 0 && product.compare_at_price > product.price;
  const discountPct = onSale ? Math.round((1 - product.price / product.compare_at_price) * 100) : 0;
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 200, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 200, damping: 20 });

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: Math.min(index, 8) * 0.05, ease }}
    >
      <Link to={`/product/${product.slug || product.id}`} data-testid={`product-card-${product.id}`} className="group block">
        <motion.div
          ref={ref}
          onMouseMove={handleMove}
          onMouseLeave={reset}
          style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }}
          className="relative overflow-hidden rounded-2xl bg-zinc-100 border border-zinc-200/70 aspect-[4/5] will-change-transform"
        >
          <motion.img
            src={img(product.images?.[0], 600)}
            alt={getAlt(product, 0, i18n.language, catLabel)}
            loading="lazy"
            className="w-full h-full object-cover"
            initial={{ scale: 1 }}
            whileHover={{ scale: 1.06 }}
            transition={{ duration: 0.8, ease }}
          />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          {onSale && (
            <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-gold text-white text-[11px] font-semibold tracking-wide shadow-sm" data-testid={`card-sale-${product.id}`}>
              −{discountPct}%
            </div>
          )}
          <div className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 group-hover:bg-zinc-900 group-hover:text-white transition-all duration-300">
            <ArrowUpRight strokeWidth={1.5} className="w-4 h-4" />
          </div>
        </motion.div>
        <div className="mt-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">{catLabel}</div>
            <h3 className="mt-1.5 font-display text-base font-medium text-zinc-900 leading-snug transition-colors group-hover:text-zinc-500">
              {productName(product, i18n.language)}
            </h3>
          </div>
          <div className="font-display text-base font-medium whitespace-nowrap text-right">
            {onSale ? (
              <div className="flex flex-col items-end leading-tight">
                <span className="text-gold">{formatPrice(product.price)}</span>
                <span className="text-zinc-400 line-through text-xs font-normal">{formatPrice(product.compare_at_price)}</span>
              </div>
            ) : (
              <span className="text-zinc-900">{formatPrice(product.price)}</span>
            )}
          </div>
        </div>
        <div className="mt-2.5 flex items-center justify-between gap-2">
          <div className="flex gap-1.5">
            {product.colors?.map((c) => (
              <span key={c} data-swatch className="w-3.5 h-3.5 rounded-full border border-zinc-300" style={{ background: colorHex[c] || "#ccc" }} title={c} />
            ))}
          </div>
          {product.review_count > 0 && (
            <div className="flex items-center gap-1 text-xs text-zinc-500" data-testid={`card-rating-${product.id}`}>
              <Star strokeWidth={1.5} className="w-3.5 h-3.5 fill-gold text-gold" />
              {product.rating} <span className="text-zinc-300">({product.review_count})</span>
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  );
};
